import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { examItemAction } from '../../../../core/components/molecules/Exam/Item';
import { FilterCategory } from '../../../../core/models/filterModal';
import { LiveSimulationItemDto } from '../../models/LiveSimulationsDto.model';
import { useStartLiveSimulation } from '../AprovaLiveSimulation/useStartLiveSimulation';
import { useFetchLiveSimulations } from './useFetchSimulations';

function useLiveExamsListTab(origin: string) {
  const filters = [
    {
      ref: 'hasAnswered',
      category_name: 'Já realizados',
      isSingleFilter: true,
      singleFilterValue: false,
    },
    {
      ref: 'notAnswered',
      category_name: 'Não realizados',
      isSingleFilter: true,
      singleFilterValue: false,
    },
  ] as FilterCategory[];

  const navigate = useNavigate();
  const fetchSimulations = useFetchLiveSimulations(origin);
  const startLiveSimulation = useStartLiveSimulation();

  const [isStartSimulationModalOpen, setIsStartSimulationModalOpen] =
    useState(false);
  const [selectedSimulation, setSelectedSimulation] =
    useState<LiveSimulationItemDto | null>(null);

  const [isModalFilterOpen, setIsModalFilterOpen] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<FilterCategory[]>([]);
  const [simulationsList, setSimulationsList] = useState<
    LiveSimulationItemDto[]
  >([]);

  const filteredSimulationsList = useMemo(() => {
    if (selectedFilters.length === 0) {
      return simulationsList;
    } else {
      return simulationsList.filter((simulation) => {
        const categoriesFilterResults: boolean[] = [];
        selectedFilters.forEach((filter) => {
          if (filter.singleFilterValue === true) {
            if (filter.ref === 'hasAnswered') {
              categoriesFilterResults.push(simulation.hasAnswered);
              return;
            }

            if (filter.ref === 'notAnswered') {
              categoriesFilterResults.push(!simulation.hasAnswered);
            }
          }
        });

        if (categoriesFilterResults.length === 0) {
          return true;
        }

        return categoriesFilterResults.some((result) => result === true);
      });
    }
  }, [selectedFilters, simulationsList]);

  useEffect(() => {
    if (fetchSimulations.data) {
      setSimulationsList(fetchSimulations.data);
    }
  }, [fetchSimulations.data]);

  function handleOpenStartModal() {
    setIsStartSimulationModalOpen(true);
  }

  function handleCloseStartModal() {
    setIsStartSimulationModalOpen(false);
  }

  async function handleSimulationAction(
    action: examItemAction,
    simulation: LiveSimulationItemDto
  ) {
    setSelectedSimulation(simulation);

    if (action === 'start' || action === 'tryagain') {
      handleOpenStartModal();
    }

    if (action === 'resume') {
      await handleStartSimulation();
    }

    if (action === 'feedback') {
      if(origin === 'challenge') {
        navigate(`/ao-vivo/desafio-60-dias/simulado/${simulation.slug}/feedback`);
      } else {
        navigate(`/ao-vivo/aprova-live/simulado/${simulation.slug}/feedback`);
      }
    }
  }

  async function handleStartSimulation() {
    const slug = selectedSimulation?.slug;

    if (slug) {
      await startLiveSimulation.mutateAsync(slug);

      if (origin === 'challenge') {
        navigate(`/ao-vivo/desafio-60-dias/simulado/${slug}`);
      } else {
        navigate(`/ao-vivo/aprova-live/simulado/${slug}`);
      }
    }
  }

  function getTimeToFinish() {
    if (!selectedSimulation) {
      return '';
    }

    const hrs = Math.floor(selectedSimulation.time / 60);
    const min = selectedSimulation.time % 60;

    let response = '';

    if (hrs > 0) {
      response += `${hrs} ${hrs > 1 ? 'horas' : 'hora'}`;
    }

    if (hrs > 0 && min > 0) {
      response += ' e ';
    }

    if (min > 0) {
      response += `${min} ${min > 1 ? 'minutos' : 'minuto'}`;
    }

    return response;
  }

  return {
    simulationsList,
    filteredSimulationsList,
    selectedSimulation,
    isStartSimulationModalOpen,
    handleOpenStartModal,
    handleCloseStartModal,
    handleSimulationAction,
    handleStartSimulation,
    getTimeToFinish,
    filters,
    isModalFilterOpen,
    setIsModalFilterOpen,
    selectedFilters,
    setSelectedFilters,
    isLoadingList: fetchSimulations.isLoading,
  };
}

export { useLiveExamsListTab };
